/**
 * VWorld(국토정보플랫폼) REST 헬퍼.
 * dev 서버는 /vworld → https://api.vworld.kr 로 프록시한다(vite.config.ts).
 * 모든 함수는 실패 시 예외를 던지지 않고 빈 결과/null을 반환한다.
 */

const KEY = import.meta.env.VITE_VWORLD_API_KEY as string | undefined;

export interface AddressResult {
  /** 표시용 주소 텍스트(도로명 우선, 없으면 지번/제목) */
  address: string;
  lat: number;
  lng: number;
}

interface VWorldSearchPoint {
  x: string; // 경도(lng)
  y: string; // 위도(lat)
}

interface VWorldSearchItem {
  title?: string;
  point: VWorldSearchPoint;
  address?: { road?: string; parcel?: string };
}

interface VWorldSearchResponse {
  response?: {
    status?: string;
    result?: { items?: VWorldSearchItem[] };
  };
}

interface VWorldAddressResponse {
  response?: {
    status?: string;
    result?: Array<{ text?: string }>;
  };
}

/** 주소 문자열 → 좌표 후보 목록 */
export async function searchAddress(query: string): Promise<AddressResult[]> {
  const q = query.trim();
  if (!KEY || !q) return [];
  const url =
    `/vworld/req/search?service=search&request=search&version=2.0` +
    `&size=10&page=1&type=address&category=road&format=json&errorformat=json` +
    `&query=${encodeURIComponent(q)}&key=${KEY}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const json = (await res.json()) as VWorldSearchResponse;
    if (json.response?.status !== 'OK') return [];
    const items = json.response.result?.items ?? [];
    return items
      .map((it): AddressResult => ({
        address: it.address?.road || it.address?.parcel || it.title || '',
        lat: Number(it.point.y),
        lng: Number(it.point.x),
      }))
      .filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng));
  } catch {
    return [];
  }
}

/** 좌표 → 주소 문자열(도로명/지번) */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  if (!KEY) return null;
  const url =
    `/vworld/req/address?service=address&request=getAddress&version=2.0` +
    `&crs=epsg:4326&type=both&format=json&errorformat=json` +
    `&point=${lng},${lat}&key=${KEY}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as VWorldAddressResponse;
    if (json.response?.status !== 'OK') return null;
    return json.response.result?.[0]?.text ?? null;
  } catch {
    return null;
  }
}
